import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { booksAPI, chatAPI, progressAPI, coachingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
    BookOpen, 
    Video, 
    GraduationCap, 
    Calendar,
    ChevronRight,
    Clock,
    Loader2,
    Plane,
    Lock,
    Check,
    Users,
    Link as LinkIcon,
    Crown,
    Sparkles,
    Gift,
    Phone
} from 'lucide-react';

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [book, setBook] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [progress, setProgress] = useState([]);
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const [vipSessionStatus, setVipSessionStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        loadDashboardData();
    }, [user]);

    const loadDashboardData = async () => {
        try {
            const [booksRes, sessionsRes] = await Promise.all([
                booksAPI.getAll(),
                chatAPI.getSessions()
            ]);

            if (booksRes.data.length > 0) {
                const firstBook = booksRes.data[0];
                setBook(firstBook);
                
                if (user?.has_book_access) {
                    try {
                        const [chaptersRes, progressRes] = await Promise.all([
                            booksAPI.getChapters(firstBook.id),
                            progressAPI.get(firstBook.id).catch(() => ({ data: [] }))
                        ]);
                        setChapters(chaptersRes.data);
                        setProgress(progressRes.data);
                    } catch (err) {
                        console.log('Could not load chapters');
                    }
                }
            }

            const upcoming = sessionsRes.data
                .filter(s => new Date(s.scheduled_at) > new Date())
                .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                .slice(0, 3);
            setUpcomingSessions(upcoming);
            
            // Load VIP session status if user is VIP
            if (user?.membership_type === 'membership_vip') {
                try {
                    const vipRes = await coachingAPI.getVipSessionStatus();
                    setVipSessionStatus(vipRes.data);
                } catch (err) {
                    console.log('Could not load VIP session status');
                }
            }
        } catch (err) {
            console.error('Failed to load dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const readingProgress = chapters.length > 0 
        ? Math.round((progress.length / chapters.length) * 100) 
        : 0;

    const hasMembership = user?.membership_type;
    const hasPremiumMembership = user?.membership_type === 'membership_premium';
    const hasVipMembership = user?.membership_type === 'membership_vip';
    const hasPremiumOrVip = hasPremiumMembership || hasVipMembership;

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-2">
                        <Plane className="h-6 w-6 text-primary" />
                        <span className="text-muted-foreground">Flight Attendant Academy</span>
                    </div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
                        Welcome back, {user?.name}!
                    </h1>
                    <p className="text-muted-foreground">
                        Track your progress and access your content
                    </p>
                </div>

                {/* Access Status Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {/* Book Access */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <BookOpen className="h-5 w-5 text-primary" />
                            {user?.has_book_access ? (
                                <Badge className="bg-green-500 text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    Owned
                                </Badge>
                            ) : (
                                <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-3 w-3 mr-1" />
                                    Locked
                                </Badge>
                            )}
                        </div>
                        <h3 className="font-medium text-sm">The Book</h3>
                        <p className="text-xs text-muted-foreground">$29.99</p>
                    </Card>

                    {/* Course Access */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <GraduationCap className="h-5 w-5 text-primary" />
                            {user?.has_course_access ? (
                                <Badge className="bg-green-500 text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    Active
                                </Badge>
                            ) : (
                                <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-3 w-3 mr-1" />
                                    Locked
                                </Badge>
                            )}
                        </div>
                        <h3 className="font-medium text-sm">Course</h3>
                        <p className="text-xs text-muted-foreground">$250</p>
                    </Card>

                    {/* Membership */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <Users className="h-5 w-5 text-primary" />
                            {hasVipMembership ? (
                                <Badge className="bg-purple-600 text-xs">
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    VIP
                                </Badge>
                            ) : hasPremiumMembership ? (
                                <Badge className="bg-yellow-500 text-xs">
                                    <Crown className="h-3 w-3 mr-1" />
                                    Premium
                                </Badge>
                            ) : hasMembership ? (
                                <Badge className="bg-green-500 text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    Basic
                                </Badge>
                            ) : (
                                <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-3 w-3 mr-1" />
                                    None
                                </Badge>
                            )}
                        </div>
                        <h3 className="font-medium text-sm">Membership</h3>
                        <p className="text-xs text-muted-foreground">
                            {hasVipMembership ? '$99/mo' : hasPremiumMembership ? '$49.99/mo' : hasMembership ? '$29/mo' : 'From $29/mo'}
                        </p>
                    </Card>

                    {/* Airlines Access */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <LinkIcon className="h-5 w-5 text-primary" />
                            {hasPremiumOrVip ? (
                                <Badge className={hasVipMembership ? "bg-purple-600 text-xs" : "bg-yellow-500 text-xs"}>
                                    {hasVipMembership ? <Sparkles className="h-3 w-3 mr-1" /> : <Crown className="h-3 w-3 mr-1" />}
                                    Access
                                </Badge>
                            ) : (
                                <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-3 w-3 mr-1" />
                                    Premium+
                                </Badge>
                            )}
                        </div>
                        <h3 className="font-medium text-sm">Airline Links</h3>
                        <p className="text-xs text-muted-foreground">Premium/VIP only</p>
                    </Card>
                </div>

                {/* VIP Free Session Card */}
                {hasVipMembership && vipSessionStatus && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <Card className={`p-6 border-2 ${vipSessionStatus.eligible ? 'border-purple-500 bg-purple-50' : 'border-green-500 bg-green-50'}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${vipSessionStatus.eligible ? 'bg-purple-100' : 'bg-green-100'}`}>
                                        <Gift className={`h-6 w-6 ${vipSessionStatus.eligible ? 'text-purple-600' : 'text-green-600'}`} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-heading text-lg font-semibold">
                                                VIP Free Coaching Session
                                            </h3>
                                            <Badge className="bg-purple-600">
                                                <Sparkles className="h-3 w-3 mr-1" />
                                                VIP Perk
                                            </Badge>
                                        </div>
                                        {vipSessionStatus.eligible ? (
                                            <p className="text-muted-foreground">
                                                You have a <strong>FREE 30-minute</strong> coaching session available this month!
                                            </p>
                                        ) : (
                                            <div>
                                                <p className="text-muted-foreground">
                                                    Your free session is booked for <strong>{vipSessionStatus.session_date}</strong> at <strong>{vipSessionStatus.session_time}</strong>
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    Status: <Badge variant="outline" className="text-xs">{vipSessionStatus.session_status}</Badge>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {vipSessionStatus.eligible ? (
                                    <Button 
                                        onClick={() => navigate('/coaching?vip_free=true')}
                                        className="bg-purple-600 hover:bg-purple-700 rounded-full"
                                        data-testid="book-vip-free-btn"
                                    >
                                        <Calendar className="h-4 w-4 mr-2" />
                                        Book Now
                                    </Button>
                                ) : vipSessionStatus.session_status === 'confirmed' && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => navigate(`/coaching/session/${vipSessionStatus.booking_id}`)}
                                        className="rounded-full"
                                        data-testid="join-vip-session-btn"
                                    >
                                        <Video className="h-4 w-4 mr-2" />
                                        View Session
                                    </Button>
                                )}
                            </div>
                        </Card>
                    </motion.div>
                )}

                {/* Quick Actions */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Reading Progress */}
                    {user?.has_book_access && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => navigate('/book')}
                            data-testid="dashboard-book"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <BookOpen className="h-5 w-5 text-primary" />
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium mb-2">Continue Reading</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Progress</span>
                                    <span className="font-medium">{progress.length}/{chapters.length}</span>
                                </div>
                                <Progress value={readingProgress} className="h-2" />
                            </div>
                        </Card>
                    )}

                    {/* Community */}
                    {(user?.has_course_access || hasMembership) && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => navigate('/live-classes')}
                            data-testid="dashboard-community"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Video className="h-5 w-5 text-primary" />
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium mb-2">Join Community</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="h-4 w-4" />
                                <span>
                                    {upcomingSessions.length > 0 
                                        ? `${upcomingSessions.length} upcoming`
                                        : 'View schedule'
                                    }
                                </span>
                            </div>
                        </Card>
                    )}

                    {/* Airlines */}
                    {hasPremiumOrVip && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => navigate('/airlines')}
                            data-testid="dashboard-airlines"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                    <LinkIcon className="h-5 w-5 text-yellow-600" />
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium mb-2">Airline Hiring Links</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Plane className="h-4 w-4" />
                                <span>40+ airlines</span>
                            </div>
                        </Card>
                    )}

                    {/* 1-on-1 Coaching (for VIP - with discount) */}
                    {hasVipMembership && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-purple-200"
                            onClick={() => navigate('/coaching')}
                            data-testid="dashboard-coaching-vip"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <Phone className="h-5 w-5 text-purple-600" />
                                </div>
                                <Badge className="bg-purple-600 text-xs">20% OFF</Badge>
                            </div>
                            <h3 className="font-medium mb-2">1-on-1 Coaching</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Sparkles className="h-4 w-4" />
                                <span>VIP discount active</span>
                            </div>
                        </Card>
                    )}

                    {/* Upgrade Prompts */}
                    {!user?.has_book_access && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow bg-primary/5"
                            onClick={() => navigate('/book')}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <BookOpen className="h-5 w-5 text-primary" />
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium mb-2">Get the Book</h3>
                            <p className="text-sm text-muted-foreground">$29.99 one-time</p>
                        </Card>
                    )}

                    {!hasMembership && (
                        <Card 
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow bg-yellow-500/5"
                            onClick={() => navigate('/membership')}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                    <Users className="h-5 w-5 text-yellow-600" />
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium mb-2">Join Membership</h3>
                            <p className="text-sm text-muted-foreground">From $29/month</p>
                        </Card>
                    )}
                </div>

                {/* Upcoming Sessions */}
                {(user?.has_course_access || hasMembership) && upcomingSessions.length > 0 && (
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-xl font-semibold">Upcoming Classes</h2>
                            <Button 
                                variant="ghost" 
                                className="rounded-full"
                                onClick={() => navigate('/live-classes')}
                            >
                                View All
                            </Button>
                        </div>
                        <div className="space-y-4">
                            {upcomingSessions.map((session) => (
                                <div 
                                    key={session.id}
                                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                                    onClick={() => navigate('/live-classes')}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Video className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{session.title}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(session.scheduled_at).toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    {session.is_active && (
                                        <Badge className="bg-green-500">Live Now</Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </motion.div>
        </div>
    );
}
